/**
 * Spark Activation API (vercel-auth-functions)
 *
 * Handles the server-side activation step that sits between access code
 * validation and Supabase OTP.
 *
 * TODO: Vercel base URLs for local, dev, qa, pr, and prod still point to dev — update when URLs are available.
 */
import * as Sentry from '@sentry/react';

import { envSwitch } from './environmentUtils';
import { logError } from './logError';

import type { LineOfBusiness } from './constants';

const sentryInfo = (message: string, data: Record<string, unknown>) => {
  Sentry.captureMessage(`${message} | ${JSON.stringify(data)}`, { level: 'info' });
};

const VERCEL_BASE_URL = envSwitch({
  local: 'https://vercel-auth-functions.vercel.app',
  dev: 'https://vercel-auth-functions.vercel.app',
  qa: 'https://vercel-auth-functions.vercel.app',
  pr: 'https://vercel-auth-functions.vercel.app',
  stage: 'https://vercel-auth-functions-stage.vercel.app',
  prod: 'https://vercel-auth-functions-prod.vercel.app',
});

interface ActivatePhoneParams {
  phone: string;
  accessCode?: string;
  product?: string;
  lob?: LineOfBusiness;
  organizationSlug?: string;
}

interface ActivatePhoneResponse {
  success: boolean;
  message: string;
  limbix_user_id: string;
}

interface ActivateConfirmParams {
  supabaseUserId: string;
  accessToken: string;
  lob?: LineOfBusiness;
  phDistinctId?: string;
}

// short-lived exchange token for native app session handoff.
interface AuthorizeRawResponse {
  code: string;
}

interface AuthorizeResponse {
  exchangeToken: string;
}

/**
 * Calls POST /api/spark/{lob}/activate/confirm to flip user_app_entitlements.status → active.
 * Must be called AFTER supabase.auth.verifyOtp() succeeds.
 */
export const activateSparkConfirm = async (
  params: ActivateConfirmParams,
): Promise<void> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/confirm`;
  const payload: Record<string, string> = { supabase_user_id: params.supabaseUserId };
  if (params.phDistinctId) {
    payload.ph_distinct_id = params.phDistinctId;
  }

  sentryInfo('[PostHog Debug][activateSparkConfirm] sending to Vercel', {
    url,
    supabaseUserIdPresent: !!params.supabaseUserId,
    ph_distinct_id: params.phDistinctId ?? null,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    logError(
      '[activateSparkConfirm]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Spark confirm failed: ${response.status}`);
  }
};

/**
 * Calls POST /api/auth/authorize to generate a short-lived exchange token.
 * Must be called AFTER refreshSession() — access_token must contain status: 'active'.
 * The returned exchange_token expires in 30 seconds.
 */
export const requestAuthCode = async (
  accessToken: string,
  refreshToken: string,
): Promise<AuthorizeResponse> => {
  const url = `${VERCEL_BASE_URL}/api/auth/authorize`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    logError('[requestAuthCode]', `failed with status: ${response.status}`, undefined, { status: response.status });
    throw new Error(`Auth authorize failed: ${response.status}`);
  }

  const data: AuthorizeRawResponse = await response.json();
  if (!data.code) {
    throw new Error('Auth authorize returned no token');
  }
  return { exchangeToken: data.code };
};

/**
 * Calls POST /api/spark/{lob}/activate/phone to register the user server-side.
 * Must be called BEFORE supabase.auth.signInWithOtp()
 */
export const activateSparkPhone = async (
  params: ActivatePhoneParams,
): Promise<ActivatePhoneResponse> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/spark/${lob}/activate/phone`;
  const payload: Record<string, string> = {
    phone: params.phone,
  };

  if (params.accessCode) {
    payload.access_code = params.accessCode;
  } else if (params.organizationSlug) {
    payload.organization_slug = params.organizationSlug;
  }

  sentryInfo('[PostHog Debug][activateSparkPhone] sending to Vercel', {
    url,
    hasAccessCode: !!params.accessCode,
    hasOrgSlug: !!params.organizationSlug,
    lob,
    ph_distinct_id_present: false,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    logError(
      '[activateSparkPhone]',
      `failed with status: ${response.status}`,
      undefined,
      { status: response.status, url },
    );
    throw new Error(`Spark activation failed: ${response.status}`);
  }

  return response.json();
};

interface ActivateEmailParams {
  email: string;
  product: string;
  accessCode?: string;
  organizationSlug?: string;
  lob?: LineOfBusiness;
  limbixUserId?: string;
  isDependent?: boolean;
  phDistinctId?: string;
}

interface ActivateEmailResponse {
  success: boolean;
  limbix_user_id: string;
}

/**
 * Calls POST /api/{product}/{lob}/activate/email to trigger a magic link email.
 *
 * Two flows:
 * - Access code flow: send { email, access_code }
 * - Direct access flow: send { email, organization_slug }
 */
export const activateEmail = async (
  params: ActivateEmailParams,
): Promise<ActivateEmailResponse> => {
  const lob = params.lob ?? 'wellness';
  const url = `${VERCEL_BASE_URL}/api/${params.product}/${lob}/activate/email`;

  const payload: Record<string, string | boolean> = {
    email: params.email,
  };

  if (params.accessCode) {
    payload.access_code = params.accessCode;
  } else if (params.organizationSlug) {
    payload.organization_slug = params.organizationSlug;
  }

  if (params.limbixUserId) {
    payload.limbix_user_id = params.limbixUserId;
  }

  if (params.isDependent) {
    payload.is_dependent = true;
  }

  if (params.phDistinctId) {
    payload.ph_distinct_id = params.phDistinctId;
  }

  sentryInfo('[PostHog Debug][activateEmail] sending to Vercel', {
    url,
    product: params.product,
    lob,
    hasAccessCode: !!params.accessCode,
    hasOrgSlug: !!params.organizationSlug,
    hasLimbixUserId: !!params.limbixUserId,
    ph_distinct_id: params.phDistinctId ?? null,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    logError(
      '[activateEmail]',
      `failed: ${response.status}`,
      undefined,
      { status: response.status, error: body.error, url },
    );
    throw new Error(body.error || `Email activation failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Calls POST /api/auth/login for returning user login.
 * Handles both phone and email — Vercel checks Supabase, migrates from Cortex
 * if needed, then triggers OTP/magic link server-side.
 *
 * Returns a uniform response regardless of whether user exists (HIPAA).
 */
export const loginUser = async (
  params: ({ phone: string; product: string } | { email: string; product: string })
    & { ph_distinct_id?: string },
): Promise<void> => {
  const url = `${VERCEL_BASE_URL}/api/auth/login`;

  sentryInfo('[PostHog Debug][loginUser] sending to Vercel', {
    url,
    hasPhone: 'phone' in params,
    hasEmail: 'email' in params,
    product: params.product,
    ph_distinct_id: String(params.ph_distinct_id ?? null),
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    console.error('[loginUser] failed:', response.status, body.error);
    throw new Error(body.error || `Login failed: ${response.status}`);
  }
};
